import {
  highlightChapters,
  STAT_SLOTS,
  type Chapter,
  type StatSlot,
} from "../events/landing";
import type { PublicEvent } from "../events/schemas";
import {
  dayKeyOf,
  formatClock,
  formatEnd,
  groupAgendaByDay,
  toDate,
} from "./agenda-schedule";
import type { AgendaItem } from "./schemas";

type HeroFields = Pick<
  PublicEvent,
  "heroKicker" | "heroHeadline" | "heroSubline" | "heroVideo"
>;

function filled(value: string | null | undefined): boolean {
  return (value ?? "").trim().length > 0;
}

/**
 * Whether the organiser set anything for the bold hero. An event with none of
 * these keeps the plain Overview, whatever else the landing page adds.
 */
export function hasHeroFields(event: HeroFields): boolean {
  return (
    filled(event.heroKicker) ||
    filled(event.heroHeadline) ||
    filled(event.heroSubline) ||
    filled(event.heroVideo)
  );
}

/** The whole days from the first to the last date, both counted, or null. */
export function eventDayCount(
  startDate: string,
  endDate: string,
): number | null {
  const day = /^\d{4}-\d{2}-\d{2}$/;
  if (!day.test(startDate) || !day.test(endDate)) return null;
  const span =
    (Date.parse(`${endDate}T00:00:00Z`) -
      Date.parse(`${startDate}T00:00:00Z`)) /
    86_400_000;
  return Number.isInteger(span) && span >= 0 ? span + 1 : null;
}

export type StatTile = { slot: StatSlot; value: string; label: string };

function plural(count: number, singular: string, many: string): string {
  return count === 1 ? singular : many;
}

/** The organiser's whole tiles, in slot order. */
export function ownStatTiles(
  event: Pick<PublicEvent, "heroStats">,
): StatTile[] {
  return STAT_SLOTS.flatMap((slot): StatTile[] => {
    const tile = event.heroStats?.[slot];
    return tile?.value.trim() && tile.label.trim()
      ? [{ slot, value: tile.value.trim(), label: tile.label.trim() }]
      : [];
  });
}

/**
 * The tiles of the number band, in slot order.
 *
 * The organiser's own tiles win, each only if it is whole. Without any, the
 * band is computed from what a member can read: the days between the event's
 * dates, the sessions on the agenda and the themes (tracks). A computed tile
 * that would read zero is left out rather than shown as "0".
 */
export function statTiles(
  event: Pick<PublicEvent, "heroStats" | "startDate" | "endDate">,
  facts: { sessions: number; themes: number },
): StatTile[] {
  const own = ownStatTiles(event);
  if (own.length > 0) return own;

  const days = eventDayCount(event.startDate, event.endDate);
  const computed: StatTile[] = [];
  if (days)
    computed.push({
      slot: "a",
      value: String(days),
      label: plural(days, "Day", "Days"),
    });
  if (facts.sessions > 0)
    computed.push({
      slot: "b",
      value: String(facts.sessions),
      label: plural(facts.sessions, "Session", "Sessions"),
    });
  if (facts.themes > 0)
    computed.push({
      slot: "c",
      value: String(facts.themes),
      label: plural(facts.themes, "Theme", "Themes"),
    });
  return computed;
}

export type EventDates = { startDate: string; endDate: string };

function dayIndex(key: string): number {
  return Date.parse(`${key}T00:00:00Z`) / 86_400_000;
}

/**
 * "Day 1", "Day 2": a day's place in the event, by its day key on the event's
 * clock against the event's first date. A day with no session keeps its
 * number, so "Day N" agrees with the number band and with the organiser's
 * titles per day. A day outside the event's dates is numbered after the
 * event's own days, in order; when the dates cannot be read the days that
 * have sessions are counted in order instead.
 */
export function dayNumbers(
  sessions: AgendaItem[],
  timeZone: string | undefined,
  dates?: EventDates,
): Map<string, number> {
  const keys = groupAgendaByDay(sessions, timeZone).days.map((day) => day.key);
  const total = dates ? eventDayCount(dates.startDate, dates.endDate) : null;
  if (!dates || total === null)
    return new Map(keys.map((key, index) => [key, index + 1]));

  let outside = 0;
  return new Map(
    keys.map((key) => {
      const place = dayIndex(key) - dayIndex(dates.startDate) + 1;
      return [key, place >= 1 && place <= total ? place : total + ++outside];
    }),
  );
}

/**
 * "10:00–12:30" on the event's clock, or just the start with no end. A
 * session that ends on a later day says so ("22:00–01:30 +1 day"), as the plain
 * schedule card does, rather than reading as a typo.
 */
export function timeSpan(
  item: AgendaItem,
  timeZone: string | undefined,
): string {
  const start = toDate(item.startsAt);
  if (!start) return "";
  const end = formatEnd(item, timeZone);
  if (!end) return formatClock(start, timeZone);
  const later =
    end.daysLater > 0
      ? ` +${end.daysLater} ${end.daysLater === 1 ? "day" : "days"}`
      : "";
  return `${formatClock(start, timeZone)}\u2013${end.clock}${later}`;
}

/**
 * "Day 2 · 10:00–12:30" on the event's clock. A one-day event leaves the day
 * out, and a session with no end shows its start alone.
 */
export function sessionWhen(
  item: AgendaItem,
  days: ReadonlyMap<string, number>,
  timeZone: string | undefined,
): string {
  const start = toDate(item.startsAt);
  if (!start) return "";
  const span = timeSpan(item, timeZone);
  const day = days.get(dayKeyOf(start, timeZone));
  // A one-day event needs no day; a later day of a longer one always does.
  return day && (days.size > 1 || day > 1) ? `Day ${day} \u00b7 ${span}` : span;
}

/** The chapters of the Overview: the first three highlights, in time order. */
export function chaptersFor(sessions: AgendaItem[]): Chapter[] {
  return highlightChapters(
    sessions.flatMap((item) => {
      const startsAt = toDate(item.startsAt);
      return startsAt ? [{ ...item, startsAt }] : [];
    }),
  );
}
